import axios from 'axios';
import {
  ID,
  ListApiResponse,
  SingleApiResponse
} from '@ianesthesie-kit/services';
import { stringifyParams } from './billing.queryParams';
import {
  BillingDashboardExportQuery,
  BillingDashboardFilters,
  BillingDashboardQuery,
  BillingLineDto,
  BillingLineEventDto,
  BillingLinePatchPayload,
  BillingPaymentDto,
  BillingReminderResultDto,
  BillingSummaryDto,
  BulkRemindPayload,
  ConfirmConsultationPayload,
  ConsultationSuggestionDto,
  ConsultationTariffEntryDto,
  DashboardExportShape,
  DashboardExportVariant,
  DoctorBillingSettingsDto,
  DoctorBillingSettingsPayload,
  LogPhoneCallPayload,
  PagedBillingDashboard,
  ReceiptVariant,
  RecordPaymentPayload,
  ReminderCadenceConfigDto,
  ReminderCadenceStepDto,
  WaivePayload
} from './billingLine.model';

/**
 * Fallback name for the dashboard export when `Content-Disposition` is missing or
 * unparseable — medical-synthesis names the file after `shape` (a lines export and a
 * payments export are not interchangeable), so the fallback must too.
 */
const EXPORT_FALLBACK_FILENAME: Record<DashboardExportShape, string> = {
  LINES: 'billing_export.csv',
  PAYMENTS: 'billing_payments_export.csv'
};

/**
 * BFF client for the Billing V0 receivables endpoints (`/billing/line`, `/billing/dashboard`,
 * `/billing/settings/*`). Hospital and user identity travel via headers — never sent from here.
 * Static-only, following {@link BillingService}'s static surface (there is no per-patient state
 * worth an instance).
 */
export class BillingLineService {
  private static BASE_URL = '/billing';

  // ---------------------------------------------------------------------------
  // PDV / line-scoped
  // ---------------------------------------------------------------------------

  public static suggestConsultationLine(
    patientId: ID,
    doctorId?: ID
  ): Promise<ConsultationSuggestionDto> {
    const qs = stringifyParams({ doctorId });
    return axios
      .get<SingleApiResponse<ConsultationSuggestionDto>>(
        `${this.BASE_URL}/${patientId}/consultation-line/suggestion${qs}`
      )
      .then((resp) => resp.data.data);
  }

  public static confirmConsultationLine(
    patientId: ID,
    payload: ConfirmConsultationPayload
  ): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/${patientId}/consultation-line`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static fetchLinesByPatient(patientId: ID): Promise<BillingLineDto[]> {
    const qs = stringifyParams({ patientId });
    return axios
      .get<ListApiResponse<BillingLineDto>>(`${this.BASE_URL}/line${qs}`)
      .then((resp) => resp.data.data);
  }

  public static patchLine(
    lineId: ID,
    payload: BillingLinePatchPayload
  ): Promise<BillingLineDto> {
    return axios
      .patch<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static waiveLine(
    lineId: ID,
    payload: WaivePayload
  ): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/waive`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static unwaiveLine(lineId: ID): Promise<BillingLineDto> {
    return axios
      .delete<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/waive`
      )
      .then((resp) => resp.data.data);
  }

  public static fetchLineEvents(lineId: ID): Promise<BillingLineEventDto[]> {
    return axios
      .get<ListApiResponse<BillingLineEventDto>>(
        `${this.BASE_URL}/line/${lineId}/events`
      )
      .then((resp) => resp.data.data);
  }

  public static recordPayment(
    lineId: ID,
    payload: RecordPaymentPayload
  ): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/payment`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static markPaidExternally(lineId: ID): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/mark-paid-externally`
      )
      .then((resp) => resp.data.data);
  }

  public static fetchLinePayments(lineId: ID): Promise<BillingPaymentDto[]> {
    return axios
      .get<ListApiResponse<BillingPaymentDto>>(
        `${this.BASE_URL}/line/${lineId}/payments`
      )
      .then((resp) => resp.data.data);
  }

  public static remindLine(lineId: ID): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/remind`
      )
      .then((resp) => resp.data.data);
  }

  public static logPhoneCall(
    lineId: ID,
    payload: LogPhoneCallPayload
  ): Promise<BillingLineDto> {
    return axios
      .post<SingleApiResponse<BillingLineDto>>(
        `${this.BASE_URL}/line/${lineId}/phone-call`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  // ---------------------------------------------------------------------------
  // Dashboard / hospital-scoped
  // ---------------------------------------------------------------------------

  public static fetchDashboard(
    query: BillingDashboardQuery
  ): Promise<PagedBillingDashboard> {
    const qs = stringifyParams(query);
    return axios
      .get<ListApiResponse<PagedBillingDashboard['rows'][number]>>(
        `${this.BASE_URL}/dashboard${qs}`
      )
      .then((resp) => ({ rows: resp.data.data, count: resp.data.count }));
  }

  public static fetchSummary(
    filters: BillingDashboardFilters
  ): Promise<BillingSummaryDto> {
    const qs = stringifyParams(filters);
    return axios
      .get<SingleApiResponse<BillingSummaryDto>>(
        `${this.BASE_URL}/summary${qs}`
      )
      .then((resp) => resp.data.data);
  }

  public static remindLines(
    payload: BulkRemindPayload
  ): Promise<BillingReminderResultDto[]> {
    return axios
      .post<ListApiResponse<BillingReminderResultDto>>(
        `${this.BASE_URL}/line/remind`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------

  public static fetchConsultationTariff(): Promise<
    ConsultationTariffEntryDto[]
  > {
    return axios
      .get<ListApiResponse<ConsultationTariffEntryDto>>(
        `${this.BASE_URL}/settings/consultation-tariff`
      )
      .then((resp) => resp.data.data);
  }

  public static upsertConsultationTariff(
    entries: ConsultationTariffEntryDto[]
  ): Promise<ConsultationTariffEntryDto[]> {
    return axios
      .put<ListApiResponse<ConsultationTariffEntryDto>>(
        `${this.BASE_URL}/settings/consultation-tariff`,
        entries
      )
      .then((resp) => resp.data.data);
  }

  public static fetchDoctorSettings(
    doctorId?: ID
  ): Promise<DoctorBillingSettingsDto> {
    const qs = stringifyParams({ doctorId });
    return axios
      .get<SingleApiResponse<DoctorBillingSettingsDto>>(
        `${this.BASE_URL}/settings/doctor${qs}`
      )
      .then((resp) => resp.data.data);
  }

  public static upsertDoctorSettings(
    payload: DoctorBillingSettingsPayload,
    doctorId?: ID
  ): Promise<DoctorBillingSettingsDto> {
    const qs = stringifyParams({ doctorId });
    return axios
      .put<SingleApiResponse<DoctorBillingSettingsDto>>(
        `${this.BASE_URL}/settings/doctor${qs}`,
        payload
      )
      .then((resp) => resp.data.data);
  }

  public static fetchReminderCadence(): Promise<ReminderCadenceConfigDto> {
    return axios
      .get<SingleApiResponse<ReminderCadenceConfigDto>>(
        `${this.BASE_URL}/settings/reminder-cadence`
      )
      .then((resp) => resp.data.data);
  }

  public static replaceReminderCadence(
    steps: ReminderCadenceStepDto[]
  ): Promise<ReminderCadenceConfigDto> {
    return axios
      .put<SingleApiResponse<ReminderCadenceConfigDto>>(
        `${this.BASE_URL}/settings/reminder-cadence`,
        steps
      )
      .then((resp) => resp.data.data);
  }

  // ---------------------------------------------------------------------------
  // Binary downloads (PDF / CSV). Same anchor-click pattern as
  // SurgicalEventService.downloadPreopEvalTemplate; the filename comes from the
  // response's Content-Disposition when present, else from the given fallback.
  // ---------------------------------------------------------------------------

  public static downloadReceipt(
    lineId: ID,
    paymentId: number,
    variant?: ReceiptVariant
  ): Promise<void> {
    const qs = stringifyParams({ variant });
    return this.downloadBlob(
      `${this.BASE_URL}/line/${lineId}/payment/${paymentId}/receipt${qs}`,
      `receipt_${paymentId}.pdf`
    );
  }

  public static downloadReminderLetter(lineId: ID): Promise<void> {
    return this.downloadBlob(
      `${this.BASE_URL}/line/${lineId}/reminder-letter`,
      `reminder_letter_${lineId}.pdf`
    );
  }

  /**
   * Downloads the CSV export. `query.lineIds` scopes it to a selection (repeated
   * `line-ids` param, ANDed with the filters); an empty selection is dropped so the
   * export stays filter-only instead of asking for zero rows. `shape` picks the row
   * grain — one row per billing line, or one row per payment for reconciling against
   * the Stripe payout statement — independently of `variant`.
   */
  public static downloadDashboardExport(
    variant: DashboardExportVariant,
    shape: DashboardExportShape,
    query: BillingDashboardExportQuery,
    signal?: AbortSignal
  ): Promise<void> {
    const { lineIds, ...filters } = query;
    const qs = stringifyParams({
      variant,
      shape,
      ...filters,
      lineIds: lineIds?.length ? lineIds : undefined
    });
    return this.downloadBlob(
      `${this.BASE_URL}/dashboard/export${qs}`,
      EXPORT_FALLBACK_FILENAME[shape],
      signal
    );
  }

  private static async downloadBlob(
    url: string,
    fallbackName: string,
    signal?: AbortSignal
  ): Promise<void> {
    const resp = await axios
      .get<Blob>(url, { responseType: 'blob', signal })
      .catch((err) => this.rethrowWithReadableError(err));
    if (signal?.aborted) {
      return;
    }
    const name =
      this.filenameFromContentDisposition(
        resp.headers['content-disposition']
      ) ?? fallbackName;
    const objUrl = URL.createObjectURL(resp.data);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
  }

  /**
   * `responseType: 'blob'` applies to error responses too, so a 4xx from the BFF arrives as a
   * `Blob` wrapping the platform's `{ error: { businessCode, message, status } }` envelope —
   * which `unwrapApiErrorDetails` / `formatErrorMessage` (hence `showErrorDialog`) cannot read,
   * degrading every failed download to the generic "unexpected error". Reads the blob back to
   * text and re-shapes `response.data` into the parsed envelope so the standard error chain
   * resolves the real business code, then rethrows the untouched Axios error. A non-JSON body
   * (HTML error page, empty gateway response) is left exactly as received.
   */
  private static async rethrowWithReadableError(err: unknown): Promise<never> {
    const response = axios.isAxiosError(err) ? err.response : undefined;
    if (!response || !(response.data instanceof Blob)) {
      throw err;
    }
    try {
      response.data = JSON.parse(await response.data.text());
    } catch {
      // Not the platform's JSON error envelope — keep the raw blob error.
    }
    throw err;
  }

  private static filenameFromContentDisposition(
    header: unknown
  ): string | undefined {
    if (typeof header !== 'string') {
      return undefined;
    }
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
  }
}
